import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/dateUtils';
import ReportWhatsAppModal from '../../components/ReportWhatsAppModal';

const PurchaseReport = () => {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const [purchases, setPurchases] = useState([]);
  const [totals, setTotals] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [firms, setFirms] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState(isAdmin ? 'ALL' : (user?.firmId || '1'));
  const [vendorId, setVendorId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAllFirms, setIsAllFirms] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [supRes, configRes] = await Promise.all([
          axiosClient.get('/accounts?type=SUPPLIER'),
          axiosClient.get('/auth/config')
        ]);
        if (supRes.success) setSuppliers(supRes.data || []);
        if (configRes.success) setFirms(configRes.data?.firms || []);
      } catch (e) {
        console.error(e);
      }
    };
    fetchInitialData();
  }, []);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const firmParam = isAdmin ? (selectedFirm || 'ALL') : (user?.firmId || '');
      const res = await axiosClient.get(
        `/reports/purchases?vendorId=${vendorId}&fromDate=${fromDate}&toDate=${toDate}&firmId=${firmParam}`
      );
      if (res.success) {
        setPurchases(res.data || []);
        setTotals(res.totals || {});
        setIsAllFirms(res.isAllFirms || firmParam === 'ALL');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGenerate();
  }, [selectedFirm]);

  return (
    <div className="container-fluid p-0">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2 no-print">
        <div>
          <h3 className="fw-bold mb-0 text-dark">
            <i className="bi bi-graph-down-arrow text-primary me-2"></i> Purchase Summary Report
          </h3>
          <p className="text-muted small mb-0">
            {isAdmin 
              ? 'Multi-Firm Admin Report: View purchases across all firms or individual branches' 
              : `Purchase Report for: ${user?.firmName || 'Assigned Branch'}`}
          </p>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-success d-flex align-items-center gap-1"
            onClick={() => setShowWhatsAppModal(true)}
            disabled={purchases.length === 0}
          >
            <i className="bi bi-whatsapp"></i> Send WhatsApp
          </button>
          <button className="btn btn-primary d-flex align-items-center gap-1" onClick={() => window.print()}>
            <i className="bi bi-printer me-1"></i> Print Report
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="card shadow-sm border-0 mb-4 no-print">
        <div className="card-body p-3">
          <div className="row g-2 align-items-end">
            {isAdmin ? (
              <div className="col-12 col-md-3">
                <label className="form-label small text-muted fw-semibold">
                  <i className="bi bi-building text-warning me-1"></i> Filter Firm Branch
                </label>
                <select
                  className="form-select"
                  value={selectedFirm}
                  onChange={(e) => setSelectedFirm(e.target.value)}
                >
                  <option value="ALL">★ All Firms (Consolidated)</option>
                  {firms.map((f) => (
                    <option key={f.Id} value={f.Id}>
                      {f.firmName}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="col-12 col-md-3">
                <label className="form-label small text-muted fw-semibold">
                  <i className="bi bi-building text-warning me-1"></i> Your Branch
                </label>
                <div className="form-control bg-light text-dark fw-semibold small text-truncate">
                  {user?.firmName || 'Assigned Firm'}
                </div>
              </div>
            )}

            <div className="col-12 col-md-3">
              <label className="form-label small text-muted">Filter Supplier</label>
              <select
                className="form-select"
                value={vendorId}
                onChange={(e) => setVendorId(e.target.value)}
              >
                <option value="">-- All Suppliers --</option>
                {suppliers.map((s) => (
                  <option key={s.Id} value={s.Id}>
                    {s.AccountName}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-6 col-md-2">
              <label className="form-label small text-muted">From Date</label>
              <input
                type="date"
                className="form-control"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="col-6 col-md-2">
              <label className="form-label small text-muted">To Date</label>
              <input
                type="date"
                className="form-control"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <div className="col-12 col-md-2">
              <button className="btn btn-ocean w-100" onClick={handleGenerate}>
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Box */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-4">
          <div className="card p-3 shadow-sm border-0 bg-primary bg-opacity-10 border-start border-4 border-primary">
            <div className="text-muted small">TOTAL PURCHASES VALUE</div>
            <h4 className="fw-bold text-primary mb-0">₹{parseFloat(totals.totalGrand || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="card p-3 shadow-sm border-0 bg-info bg-opacity-10 border-start border-4 border-info">
            <div className="text-muted small">TOTAL ITEMS SUBTOTAL</div>
            <h4 className="fw-bold text-dark mb-0">₹{parseFloat(totals.totalSubtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="card p-3 shadow-sm border-0 bg-success bg-opacity-10 border-start border-4 border-success">
            <div className="text-muted small">TOTAL DISCOUNTS</div>
            <h4 className="fw-bold text-success mb-0">₹{parseFloat(totals.totalDiscount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
          </div>
        </div>
      </div>

      {/* Report Table */}
      <div className="card shadow-sm border-0 print-area">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0 small">
              <thead className="table-light">
                <tr>
                  <th>Pur #</th>
                  <th>Date</th>
                  {isAllFirms && <th>Firm Branch</th>}
                  <th>Supplier Name</th>
                  <th>Inv No</th>
                  <th>Type</th>
                  <th className="text-end">Subtotal</th>
                  <th className="text-end">Discount</th>
                  <th className="text-end">Grand Total</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={isAllFirms ? 9 : 8} className="text-center py-5">
                      <div className="spinner-border text-primary" role="status"></div>
                    </td>
                  </tr>
                ) : purchases.length === 0 ? (
                  <tr>
                    <td colSpan={isAllFirms ? 9 : 8} className="text-center py-5 text-muted">No purchase records found.</td>
                  </tr>
                ) : (
                  purchases.map((p) => (
                    <tr key={p.Id}>
                      <td><strong>#{p.purchaseNo}</strong></td>
                      <td>{formatDate(p.purchaseDate)}</td>
                      {isAllFirms && (
                        <td>
                          <span className="badge bg-light text-secondary border">{p.firmName || `Firm #${p.FirmId}`}</span>
                        </td>
                      )}
                      <td className="fw-bold text-dark">{p.SupplierName || 'Supplier'}</td>
                      <td>{p.InvoiceNo || '-'}</td>
                      <td>
                        <span className={`badge ${p.invoiceType === 0 ? 'bg-success' : 'bg-warning text-dark'}`}>
                          {p.invoiceType === 0 ? 'Cash' : 'Credit'}
                        </span>
                      </td>
                      <td className="text-end">₹{parseFloat(p.subtotal || 0).toFixed(2)}</td>
                      <td className="text-end text-danger">-₹{parseFloat(p.discAmount || 0).toFixed(2)}</td>
                      <td className="text-end fw-bold text-primary">₹{parseFloat(p.GranadTotal || 0).toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* WhatsApp Sharing Modal */}
      {showWhatsAppModal && (
        <ReportWhatsAppModal
          show={showWhatsAppModal}
          onClose={() => setShowWhatsAppModal(false)}
          title="Purchase Summary Report"
          reportType="purchase_report"
          reportData={{
            title: 'Purchase Summary Report',
            subtitle: `Period: ${fromDate ? formatDate(fromDate) : 'Start'} to ${toDate ? formatDate(toDate) : 'Present'}`,
            fromDate,
            toDate,
            totals,
            columns: ['Pur #', 'Date', 'Supplier', 'Inv No', 'Subtotal', 'Grand Total'],
            rows: purchases.map(p => [
              `#${p.purchaseNo}`,
              formatDate(p.purchaseDate),
              p.SupplierName || 'Supplier',
              p.InvoiceNo || '-',
              `₹${parseFloat(p.subtotal || 0).toFixed(2)}`,
              `₹${parseFloat(p.GranadTotal || 0).toFixed(2)}`
            ]),
            summaryCards: [
              { label: 'Total Purchases', value: `₹${parseFloat(totals.totalGrand || 0).toLocaleString('en-IN')}` },
              { label: 'Subtotal Items', value: `₹${parseFloat(totals.totalSubtotal || 0).toLocaleString('en-IN')}` },
              { label: 'Total Discounts', value: `₹${parseFloat(totals.totalDiscount || 0).toLocaleString('en-IN')}` }
            ],
            firmName: user?.firmName || 'PUSHPAI SEA FOOD(ALIBAG)'
          }}
        />
      )}
    </div>
  );
};

export default PurchaseReport;
